// client/src/pages/Admin/ProjectManager/components/ProjectPagination.tsx
/**
 * PROJECT PAGINATION COMPONENT
 * 
 * Pagination controls for project list.
 * 
 * @module ProjectManager/Components/ProjectPagination
 */

import React from 'react';
import { PAGE_SIZE_OPTIONS } from '../constants/project.constants';

interface ProjectPaginationProps {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    startIndex: number;
    endIndex: number;
    totalItems: number;
    pageNumbers: (number | string)[];
    onGoToPage: (page: number) => void;
    onGoToFirstPage: () => void;
    onGoToLastPage: () => void;
    onGoToPreviousPage: () => void;
    onGoToNextPage: () => void;
    onPageSizeChange: (size: number) => void;
}

export const ProjectPagination: React.FC<ProjectPaginationProps> = ({
    currentPage,
    totalPages,
    pageSize,
    startIndex,
    endIndex,
    totalItems,
    pageNumbers,
    onGoToPage,
    onGoToFirstPage,
    onGoToLastPage,
    onGoToPreviousPage,
    onGoToNextPage,
    onPageSizeChange
}) => {
    if (totalItems === 0) return null;

    return (
        <div className="pagination-container">
            <div className="pagination-info">
                <span className="pagination-text">
                    Showing {startIndex} to {endIndex} of {totalItems} entries
                </span>
                <div className="pagination-size-control">
                    <label htmlFor="page-size" className="page-size-label">
                        Rows per page:
                    </label>
                    <select
                        id="page-size"
                        className="page-size-select"
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        aria-label="Rows per page"
                    >
                        {PAGE_SIZE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <nav className="pagination-controls" aria-label="Pagination">
                <button
                    className="pagination-btn"
                    onClick={onGoToFirstPage}
                    disabled={currentPage === 1}
                    aria-label="First page"
                >
                    ⏮
                </button>
                <button
                    className="pagination-btn"
                    onClick={onGoToPreviousPage}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                >
                    ◀
                </button>

                {pageNumbers.map((page, index) => (
                    typeof page === 'number' ? (
                        <button
                            key={index}
                            className={`pagination-btn page-number ${currentPage === page ? 'active' : ''}`}
                            onClick={() => onGoToPage(page)}
                            aria-label={`Page ${page}`}
                            aria-current={currentPage === page ? 'page' : undefined}
                        >
                            {page}
                        </button>
                    ) : (
                        <span key={index} className="pagination-ellipsis">
                            {page}
                        </span>
                    )
                ))}

                <button
                    className="pagination-btn"
                    onClick={onGoToNextPage}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                >
                    ▶
                </button>
                <button
                    className="pagination-btn"
                    onClick={onGoToLastPage}
                    disabled={currentPage === totalPages}
                    aria-label="Last page"
                >
                    ⏭
                </button>
            </nav>
        </div>
    );
};